import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useApi, type Course as CourseType, type Lesson } from '../hooks/useApi';
import LessonCard from '../components/LessonCard';
import { Loader2, ArrowLeft, Brain, Bot, Eye } from 'lucide-react';

interface CourseData {
  course: CourseType;
  track: { id: string; slug: string; title: string } | null;
  lessons: Lesson[];
}

export default function Course() {
  const { slug } = useParams<{ slug: string }>();
  const { fetchApi } = useApi();
  const [data, setData] = useState<CourseData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadCourse();
  }, [slug]);

  async function loadCourse() {
    setLoading(true);
    try {
      const result = await fetchApi<CourseData>(`/api/courses/${slug}`);
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load course');
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-accent-blue" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="text-center py-12">
        <p className="text-red-400">{error || 'Course not found'}</p>
        <Link
          to="/"
          className="mt-4 inline-flex items-center gap-2 text-accent-blue hover:underline"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to tracks
        </Link>
      </div>
    );
  }

  const { course, track, lessons } = data;

  return (
    <div>
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-sm text-gray-400 mb-6">
        <Link to="/" className="hover:text-white transition-colors">
          Tracks
        </Link>
        <span>/</span>
        {track && (
          <>
            <Link to={`/tracks/${track.slug}`} className="hover:text-white transition-colors">
              {track.title}
            </Link>
            <span>/</span>
          </>
        )}
        <span className="text-gray-300">{course.title}</span>
      </nav>

      {/* Course Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-3">
          {course.title}
        </h1>
        <p className="text-lg text-gray-400 max-w-3xl">
          {course.description}
        </p>
      </div>

      {/* Mode Legend */}
      <div className="bg-background-secondary rounded-xl p-4 border border-gray-800 mb-8">
        <h3 className="text-sm font-medium text-gray-300 mb-3">Learning Modes</h3>
        <div className="flex flex-wrap gap-4">
          <div className="flex items-center gap-2 text-sm">
            <div className="p-1.5 rounded bg-amber-400/10">
              <Brain className="w-4 h-4 text-amber-400" />
            </div>
            <span className="text-gray-400">No AI Tools - Practice independent thinking</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <div className="p-1.5 rounded bg-accent-blue/10">
              <Bot className="w-4 h-4 text-accent-blue" />
            </div>
            <span className="text-gray-400">AI Assisted - Use AI tools freely</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <div className="p-1.5 rounded bg-accent-purple/10">
              <Eye className="w-4 h-4 text-accent-purple" />
            </div>
            <span className="text-gray-400">AI Audit - Critically evaluate outputs</span>
          </div>
        </div>
      </div>

      {/* Lessons */}
      <section>
        <h2 className="text-xl font-semibold text-white mb-4">
          Lessons ({lessons.length})
        </h2>
        {lessons.length === 0 ? (
          <div className="bg-background-secondary rounded-xl p-8 text-center border border-gray-800">
            <p className="text-gray-400">No lessons available yet. Check back soon!</p>
          </div>
        ) : (
          <div className="space-y-3">
            {lessons.map((lesson, index) => (
              <div key={lesson.id} className="flex items-center gap-3">
                <span className="text-sm text-gray-500 w-6 text-right">{index + 1}</span>
                <div className="flex-1">
                  <LessonCard lesson={lesson} />
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
